import { useQuery } from '@tanstack/react-query';
import { hostsCountQueryOptions } from '#api/hosts.queries.ts';
import type { GetHostsFilter } from '#api/hosts.ts';
import { Link } from '#components/atoms/link/Link.tsx';

/**
 * The hosts-list URL for a filter. Derived from the same filter the count is fetched with, so the
 * number and the list it opens cannot drift apart. Note the URL params are the frontend's own
 * (`filter.tags`, plural and repeated), not the wire params `getHosts` sends.
 */
function hostsFilterPath(filter: GetHostsFilter) {
  const params = new URLSearchParams();
  if (filter.roleID) {
    params.set('filter.roleID', String(filter.roleID));
  }
  if (filter.networkID) {
    params.set('filter.networkID', String(filter.networkID));
  }
  for (const tag of filter.tags ?? []) {
    params.append('filter.tags', tag);
  }
  return `/hosts?${params.toString()}`;
}

function pluralizeHosts(count: number) {
  return `${count} ${count === 1 ? 'host' : 'hosts'}`;
}

/**
 * How many hosts match a filter, linking to the same list it counts. The count is fetched per
 * distinct filter: several rows can name the same hosts, and React Query keys on the filter, so
 * those rows share a request instead of taking one each. While it loads or if it fails the link
 * stays and simply goes unnumbered — a missing count should not cost the user the way to enumerate
 * members.
 */
export function HostsCountLink({ filter }: { filter: GetHostsFilter }) {
  const { data: count } = useQuery(hostsCountQueryOptions(filter));

  return (
    <Link className="a inline nowrap" to={hostsFilterPath(filter)}>
      {count === undefined ? 'View hosts' : pluralizeHosts(count)}
    </Link>
  );
}
